
import { defineComponent, reactive, ref, computed, onMounted } from "vue-demi";
import selects from "@/utils/selects";
import axios from "@/plugins/http";
import { API_ROUTES } from "@/utils/API";
import { generateUUID } from "@/utils/generateUUID";
import {
  API_TRoleTemplate,
  convertJobTemplatesToSelect,
  TRoleTemplate_Dropdown,
} from "@/models/role_templates";
import { SMUser } from "@/store";
import { emailRules, isEmailValid } from "@/utils/validator";

export default defineComponent({
  name: "CoSAllocation",

  components: {
    Popup: () => import("@/modules/Pricing/Popup/Popup.vue"),
    FormsTemplate: () => import("@/modules/Pricing/Services/Forms/FormsTemplate.vue"),
    JobRoleSearch: () => import("@/modules/Pricing/Services/Forms/components/JobRoleSearch.vue"),
  },

  emits: ["ok", "cancel", "close"],

  setup(_, { emit }) {
    const cosAlloactionItemTemplate = () => ({
      id: generateUUID(),
      soc_code: "",
      soc_name: "",
      job_title: "",
      job_description: "",
      salary: "",
      hours_of_work: "",
      candidate_name: "",
      nationality: "",
      current_uk_visa: "",
      notes: "",
    });

    const data = reactive({
      service_type: "cos_allocation",
      employer_name:
        `${SMUser.user?.title} ${SMUser.user?.first_name} ${SMUser.user.last_name}` || "",
      phone: `${SMUser.user?.phone}` || "",
      email: `${SMUser.user?.email}` || "",
      company_name: `${SMUser.user?.company?.CompanyProfile?.name}` || "",
      additional: {
        priority_processing: false,
        cos_allocation_item_list: [cosAlloactionItemTemplate()],
      },
    });

    const isPopoverOpen = ref(true);

    const onOk = () => {
      emit("ok");
    };

    const onCancel = () => {
      isPopoverOpen.value = false;

      emit("cancel");
    };

    const onClose = () => {
      isPopoverOpen.value = false;

      emit("close");
    };

    const countries = computed(() => {
      return selects.countries.map((option) => ({ label: option, value: option }));
    });

    const loopKey = ref(0);

    function addCosAllocationItem() {
      data.additional.cos_allocation_item_list.push(cosAlloactionItemTemplate());
    }

    function removeCosAllocationItem(index: number) {
      if (index >= 0 && index < data.additional.cos_allocation_item_list.length) {
        data.additional.cos_allocation_item_list.splice(index, 1);

        loopKey.value += 1;
      }
    }

    const itemsLength = computed(() => data.additional.cos_allocation_item_list.length);

    const isLooped = computed(() => itemsLength.value > 1);

    const jobTemplates = ref<TRoleTemplate_Dropdown[]>([]);

    const jobTemplatesLoading = ref(false);

    async function fetchJobTemplates() {
      try {
        jobTemplatesLoading.value = true;

        const params = {
          archive: false,
        };

        const { data } = await axios.get(API_ROUTES.ROLE_TEMPLATES(), { params });

        if (!data) return;

        if (!data) return;

        const onlyCompleted = data.filter((item) => item.completed === true);

        jobTemplates.value = onlyCompleted.map((item: API_TRoleTemplate) =>
          convertJobTemplatesToSelect(item)
        );
      } finally {
        jobTemplatesLoading.value = false;
      }
    }

    function setJobRole(
      { label, code, description }: { label: string; code: number; description: string },
      index: number
    ) {
      data.additional.cos_allocation_item_list[index].soc_name = label;
      data.additional.cos_allocation_item_list[index].soc_code = String(code);
      data.additional.cos_allocation_item_list[index].job_description = description;
    }

    onMounted(() => {
      fetchJobTemplates();
    });

    const isFormComplete = computed(() => {
      const topLevelRequiredFields = [
        data.employer_name,
        data.phone,
        data.email,
        data.company_name,
      ];

      const areTopFieldsFilled = topLevelRequiredFields.every(
        (val) => !!val && String(val).trim().length > 0
      );

      const areAllAllocationsValid = data.additional.cos_allocation_item_list.every((item) => {
        return [
          item.soc_code,
          item.soc_name,
          // item.job_title,
          item.job_description,
          item.salary,
          item.hours_of_work,
          item.candidate_name,
          item.nationality,
          item.current_uk_visa,
        ].every((val) => !!val && String(val).trim().length > 0);
      });

      return isEmailValid.value && areTopFieldsFilled && areAllAllocationsValid;
    });

    const loading = ref(false);

    async function submitServiceRequest() {
      try {
        loading.value = true;

        await axios.post(API_ROUTES.SUBMIT_SERVICE_REQUEST, data);
      } catch (error) {
        console.log("SUBMIT_SERVICE_REQUEST failed", error);
      } finally {
        loading.value = false;

        onOk();
      }
    }

    const emailValidationRules = emailRules;

    return {
      data,
      isPopoverOpen,
      onOk,
      onCancel,
      onClose,
      countries,
      jobTemplates,
      jobTemplatesLoading,
      setJobRole,
      itemsLength,
      loopKey,
      isLooped,
      addCosAllocationItem,
      removeCosAllocationItem,
      isFormComplete,
      loading,
      submitServiceRequest,
      emailValidationRules,
    };
  },
});
